"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { Collection } from "@/lib/types"
import { cn, getPlaceHolderDishImage } from "@/lib/utils"

interface HomeCollectionCarouselProps {
  collections: Collection[]
}

export function HomeCollectionCarousel({
  collections,
}: HomeCollectionCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [maxScroll, setMaxScroll] = useState(0)

  // Update scroll info and responsive settings
  useEffect(() => {
    const updateScrollInfo = () => {
      if (scrollContainerRef.current) {
        // Update max scroll
        const container = scrollContainerRef.current
        setMaxScroll(container.scrollWidth - container.clientWidth)
      }
    }

    updateScrollInfo()
    window.addEventListener("resize", updateScrollInfo)
    return () => window.removeEventListener("resize", updateScrollInfo)
  }, [collections])

  // Track scroll position
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft)
    }
  }

  // Simple navigation functions
  const scrollPrev = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const itemWidth =
        container.firstElementChild?.firstElementChild?.clientWidth || 0
      const currentScroll = container.scrollLeft
      const targetScroll =
        Math.floor(currentScroll / itemWidth) * itemWidth - itemWidth

      container.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: "smooth",
      })
    }
  }

  const scrollNext = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const itemWidth =
        container.firstElementChild?.firstElementChild?.clientWidth || 0
      const currentScroll = container.scrollLeft
      const targetScroll =
        Math.ceil(currentScroll / itemWidth) * itemWidth + itemWidth

      container.scrollTo({
        left: Math.min(targetScroll, maxScroll),
        behavior: "smooth",
      })
    }
  }

  const isAtStart = scrollPosition <= 10
  const isAtEnd = maxScroll > 0 && scrollPosition >= maxScroll - 10

  return (
    <div>
      <div className="flex justify-between items-center md:mb-4 mb-2">
        <h2 className="text-xl lg:text-3xl font-semibold tracking-tight text-gray-800">
          Bộ sưu tập món ăn
        </h2>
        <div className="flex gap-2">
          <button
            onClick={scrollPrev}
            disabled={isAtStart}
            className={cn(
              "p-1.5 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors",
              isAtStart ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            )}
            aria-label="Previous collections"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={scrollNext}
            disabled={isAtEnd}
            className={cn(
              "p-1.5 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors",
              isAtEnd ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            )}
            aria-label="Next collections"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Simplified scrollable container */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto scrollbar-hide"
        onScroll={handleScroll}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          scrollSnapType: "x mandatory",
        }}
        role="region"
        aria-label="Bộ sưu tập món ăn"
      >
        <div className="grid grid-flow-col auto-cols-[70%] sm:auto-cols-[calc(100%/3-0.67rem)] md:auto-cols-[calc(100%/3-0.75rem)] lg:auto-cols-[calc(100%/4-0.75rem)] gap-4">
          {collections.map((item, index) => (
            <div key={item.id} className="snap-start">
              <Link href={`/collection?id=${item.id}&name=${item.name}`}>
                <article
                  className="relative w-full aspect-[16/8] rounded-xl group transition-transform duration-300 ease-out cursor-pointer overflow-hidden"
                  itemScope
                  itemType="https://schema.org/Collection"
                >
                  <Image
                    src={
                      item.web_image_compressed ||
                      "/images/collection-placeholder.jpg"
                    }
                    alt={`Hình ảnh bộ sưu tập ${item.name}`}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, (max-width: 1280px) 20vw, 16vw"
                    className="object-cover w-full h-auto transition-transform duration-300 ease-out group-hover:scale-110"
                    itemProp="image"
                    loading={index < 4 ? "eager" : "lazy"}
                    priority={index < 4}
                    placeholder="blur"
                    blurDataURL={getPlaceHolderDishImage()}
                    quality={97}
                  />

                  <div className="absolute bottom-0 left-0 right-0 px-2 pb-2 pt-6 text-white bg-gradient-to-t from-black/80 rounded-b-xl to-transparent to-100% transition-opacity duration-300 group-hover:opacity-90">
                    <h2 className="text-lg font-bold" itemProp="name">
                      {item.name}
                    </h2>
                    {item.description && (
                      <p
                        className="text-sm line-clamp-1"
                        itemProp="description"
                      >
                        {item.description}
                      </p>
                    )}
                  </div>
                </article>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Hidden div with all collections for SEO */}
      <div className="sr-only" aria-hidden="true">
        <h3>Bộ sưu tập món ăn</h3>
        <ul>
          {collections.map((item) => (
            <li key={`seo-${item.id}`}>
              <Link href={`/collection?id=${item.id}&name=${item.name}`}>
                {item.name} - {item.description}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
